import {ExpressCheckoutElement, useElements, useStripe} from '@stripe/react-stripe-js'
import type {
  ClickResolveDetails,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
} from '@stripe/stripe-js'
import Big from 'big.js'
import pRetry from 'p-retry'

import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import useCartSummary, {getTaxAndDiscount} from '@/lib/cart/hooks/useCartSummary'
import {useCountry} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import {CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT} from '@/lib/payment/constants'
import useCustomerSession from '@/lib/payment/hooks/useCustomerSession'
import {StripeAddress} from '@/lib/payment/paymenModel'
import {useSetElementsOptions} from '@/lib/payment/PaymentProvider'
import {createPaymentIntent} from '@/lib/payment/paymentService'
import {getRoundUp, useRoundUp, useSetCustomItems} from '@/lib/payment/store'
import toStripeAmountNumber from '@/lib/payment/utils/toStripeAmountNumber'
import {CheckoutResult} from '@/models/cartModel'
import {OrderStatus} from '@/models/orderModel'
import {CheckoutRoute} from '@/routeRegistry'
import {buildStringForCalibratedError, isCartCalibratedError} from '@/services/cartService'
import {confirmOrder, getOrder} from '@/services/orderService'
import UnexpectedError from '@/utils/api/UnexpectedError'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

interface Props {
  applyRewards: boolean
  custom?: boolean
  className?: string
  coupon?: string
}

const options: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    amazonPay: 'auto',
    applePay: 'always',
    googlePay: 'always',
    link: 'auto',
    paypal: 'auto',
  },
  layout: {
    maxColumns: 1,
    overflow: 'never',
  },
}

export default memo<Props>(function ExpressCheckout({applyRewards, custom, className, coupon}) {
  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()
  const latestApplyRewards = useLatest(applyRewards)
  const [country] = useCountry()
  const latestCountry = useLatest(country)
  const [address, setAddress] = useState<StripeAddress | undefined>(undefined)
  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const latestLocale = useLatest(locale)
  const latestCustom = useLatest(custom)
  const setCustomItems = useSetCustomItems()
  const isAuthenticated = useIsAuthenticated()
  const latestIsAuthenticated = useLatest(isAuthenticated)
  const latestCoupon = useLatest(coupon)
  const store = useStore()
  const [roundUp] = useRoundUp()
  const latestRoundUp = useLatest(roundUp)

  const setElementsOptions = useSetElementsOptions()

  const {data: customerSession} = useCustomerSession()

  const {total, totalBeforeTax, cart, derivedDiscountValue, tax, roundUpAmount} = useCartSummary(
    applyRewards,
    coupon ?? '',
    undefined,
    address,
    custom,
  )

  const latestDerivedDiscountValue = useLatest(derivedDiscountValue)
  const latestTax = useLatest(tax)
  const latestTotalBeforeTax = useLatest(totalBeforeTax)
  const latestItems = useLatest(cart?.originalItems)
  const latestRoundUpAmount = useLatest(roundUpAmount)

  ;(function updateElementsOptions() {
    setElementsOptions(options => {
      options.mode = 'payment'
      options.setupFutureUsage = 'off_session' // vs on?
      if (customerSession) {
        options.customerSessionClientSecret = customerSession
      }
      options.amount = toStripeAmountNumber(total)
    })
  })()

  const latestLineItems = useLatest(cart?.originalItems)

  const expressLineItems = Object.values(cart?.items ?? {}).reduce<
    {name: string; amount: number}[]
  >((acc, item) => {
    item.forEach(item => {
      acc.push({
        name: `${item.title} x ${item.quantity}`,
        amount: toStripeAmountNumber(Big(item.price).mul(item.quantity)),
      })
    })
    return acc
  }, [])
  const latestExpressLineItems = useLatest(expressLineItems)

  const onClick = useCallback((event: StripeExpressCheckoutElementClickEvent) => {
    const options: ClickResolveDetails = {
      allowedShippingCountries: [latestCountry.current],
      shippingAddressRequired: true,
      emailRequired: true,
      phoneNumberRequired: true,
      lineItems: [
        ...latestExpressLineItems.current,
        {
          name: 'Tax',
          amount: toStripeAmountNumber(latestTax.current),
        },
        latestRoundUp.current
          ? {
              name: 'Donate change to NFB',
              amount: toStripeAmountNumber(latestRoundUpAmount.current),
            }
          : null,
        {
          name: 'Discount',
          amount: toStripeAmountNumber(Big(-latestDerivedDiscountValue.current)),
        },
      ].filter(Boolean),
      shippingRates: [
        {
          id: 'free-shipping',
          displayName: 'Free shipping',
          amount: 0,
          // deliveryEstimate: {
          //   maximum: {unit: 'day', value: 5},
          //   minimum: {unit: 'day', value: 3},
          // } as const,
        },
      ],
      // applePay: {
      //   recurringPaymentRequest: {
      //     paymentDescription: 'My subscription',
      //     managementURL: 'https://example.com/billing',
      //     regularBilling: {
      //       amount: 2500,
      //       label: 'Monthly subscription fee',
      //       recurringPaymentIntervalUnit: 'month',
      //       recurringPaymentIntervalCount: 1,
      //     },
      //   },
      // } as const,
    }

    console.log('click', options)

    setTimeout(() => {
      event.resolve(options)
    }, 0)
  }, [])

  const onShippingAddressChange = useCallback(
    async (event: StripeExpressCheckoutElementShippingAddressChangeEvent) => {
      try {
        const address = StripeAddress({
          line1: '',
          city: event.address.city,
          state: event.address.state,
          postalCode: event.address.postal_code,
          country: event.address.country,
        })

        setAddress(address)

        function resolve(tax: Big, discount: Big, roundUp: boolean, roundUpAmount: Big) {
          setElementsOptions(options => {
            options.amount = toStripeAmountNumber(
              latestTotalBeforeTax.current.plus(tax).plus(roundUpAmount).plus(discount.times(-1)),
            )
          })

          setTimeout(() => {
            const resolveDetails = {
              lineItems: [
                ...latestExpressLineItems.current,
                {
                  name: 'Tax',
                  amount: toStripeAmountNumber(tax),
                },
                roundUp
                  ? {
                      name: 'Donate change to NFB',
                      amount: toStripeAmountNumber(roundUpAmount),
                    }
                  : null,
                {
                  name: 'Discount',
                  amount: toStripeAmountNumber(discount.times(-1)),
                },
              ].filter(Boolean),
            }
            console.log('line items updated', resolveDetails)
            event.resolve(resolveDetails)
          }, 1000)
        }

        const {tax, discount, roundUp, roundUpAmount} = await getTaxAndDiscount({
          queryClient,
          roundUp: getRoundUp(store),
          taxQueryParams: {
            address,
            lineItems: latestLineItems.current ?? [],
            locale: latestLocale.current,
          },
        })

        resolve(tax, discount, roundUp, roundUpAmount)
      } catch (e) {
        console.error(e)
        event.reject()
      }
    },
    [store, setElementsOptions, queryClient],
  )

  // const onShippingRateChange = (event: StripeExpressCheckoutElementShippingRateChangeEvent) => {
  //   console.log(event)
  //   event.resolve({})
  // }

  const onConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) return

      try {
        const {error: submitError} = await elements.submit()
        if (submitError) {
          toastErrorMessage(submitError)
          console.error(submitError)
          return
        }

        // Create a ConfirmationToken using the details collected by the Express Checkout Element
        const {error: confirmationError, confirmationToken} = await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              allow_redisplay: 'always',
            },
          },
        })

        const confirmationTokenId = confirmationToken?.id

        if (confirmationError || !confirmationTokenId) {
          toastErrorMessage(confirmationError)
          console.error(confirmationError)
          return
        }

        const returnUrl = router.buildLocation({
          to: CheckoutRoute.fullPath,
          params: {
            result: CheckoutResult.Success,
          },
          search: {
            guest: !latestIsAuthenticated.current ? true : undefined,
          },
        }).href

        const {clientSecret, order, paymentIntentStatus} = await createPaymentIntent({
          isAuthenticated: latestIsAuthenticated.current,
          confirmationTokenId,
          applyRewards: latestApplyRewards.current,
          returnUrl,
          queryClient,
          locale: latestLocale.current,
          items: latestCustom.current ? latestItems.current : undefined,
          setCustomItems,
          email: event.billingDetails?.email ?? '',
          coupon: latestCoupon.current,
          roundUp: getRoundUp(store),
        })

        if (clientSecret !== CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT) {
          if (paymentIntentStatus === 'requires_action') {
            const {error: nextActionError} = await stripe.handleNextAction({clientSecret})

            if (nextActionError) {
              // eslint-disable-next-line @typescript-eslint/only-throw-error -- its okay
              throw nextActionError
            }

            await confirmOrder({
              orderId: order.id,
              locale: latestLocale.current,
            })
          }
        }

        const updatedOrder = await getOrder({
          orderId: order.id,
          locale: latestLocale.current,
        })

        if (updatedOrder === false) {
          throw new UnexpectedError()
        }

        if (updatedOrder.status === OrderStatus.AWAITING_PAYMENT) {
          await pRetry(
            () =>
              confirmOrder({
                orderId: order.id,
                locale: latestLocale.current,
              }),
            {retries: 3},
          )
        }

        await router.navigate({
          to: CheckoutRoute.fullPath,
          params: {
            result: CheckoutResult.Success,
          },
          search: {
            guest: !latestIsAuthenticated.current ? true : undefined,
            orderId: order.id,
          },
        })
      } catch (e) {
        if (isCartCalibratedError(e)) {
          toastErrorMessage(buildStringForCalibratedError(e))
        } else {
          console.error(e)
          toastErrorMessage(e)
        }
      }
    },
    [store, elements, queryClient, router, setCustomItems, stripe],
  )

  // const onCancel = () => {
  //   console.log('canceled')
  // }

  if (total.toNumber() <= 0) {
    return null
  }

  return (
    <div className={className}>
      <ExpressCheckoutElement
        options={options}
        onConfirm={onConfirm}
        onClick={onClick}
        onShippingAddressChange={onShippingAddressChange}
        // onShippingRateChange={onShippingRateChange}
        // onCancel={onCancel}
      />
    </div>
  )
})
